export const CLOSING_HOUR = 18;
export const MIN_BILLABLE_HOURS = 1;
export const DEFAULT_HOURLY_RATE = 8;

export const getHourlyRateFromRaw = (rawAmount) => {
  const raw = Number(rawAmount);
  if (!Number.isFinite(raw) || raw <= 0) {
    return DEFAULT_HOURLY_RATE;
  }
  return raw / 100;
};

export const parseEventStart = (isoString) => {
  if (!isoString) {
    return null;
  }
  const date = new Date(isoString);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const getClosingTime = (startDate) => {
  const end = new Date(startDate);
  end.setHours(CLOSING_HOUR, 0, 0, 0);
  return end;
};

export const calculateBillableHours = (startDate, endDate) => {
  const diffMs = endDate.getTime() - startDate.getTime();
  if (diffMs <= 0) {
    return MIN_BILLABLE_HOURS;
  }

  const totalMinutes = diffMs / (1000 * 60);
  const hours = Math.ceil(totalMinutes / 60);
  return Math.max(MIN_BILLABLE_HOURS, hours);
};

const PLATE_PARAM_KEYS = [
  'vehicle_plate',
  'plate_number',
  'vehicles_plate_number',
  'vehicles_registration_number',
  'vehicle_registration_number',
  'registration_number',
  'license_plate',
  'lpn',
  'vehicles_number',
  'vehicle_number'
];

const NAMIBIAN_PLATE_PATTERN = /^[A-Z]{1,3}\d{3,4}[A-Z]{0,3}$/i;

const normalizePlate = (value) => {
  if (value === undefined || value === null) {
    return null;
  }

  const trimmed = String(value).trim();
  return trimmed ? trimmed.toUpperCase() : null;
};

const looksLikePlate = (value) => NAMIBIAN_PLATE_PATTERN.test(String(value).trim());

/**
 * O'City pan values are often formatted as "{accountId}_{plate}" — return the plate part only.
 */
export const extractPlateFromPan = (pan) => {
  const normalized = normalizePlate(pan);
  if (!normalized) {
    return null;
  }

  if (normalized.includes('_')) {
    const platePart = normalizePlate(normalized.split('_').pop());
    if (platePart && looksLikePlate(platePart)) {
      return platePart;
    }
  }

  if (looksLikePlate(normalized)) {
    return normalized;
  }

  return null;
};

export const getVehiclePlate = (attributes = {}, searchKey = '') => {
  const params = attributes.params || {};

  for (const key of PLATE_PARAM_KEYS) {
    const plate = normalizePlate(params[key]);
    if (plate) {
      return plate;
    }
  }

  for (const [key, value] of Object.entries(params)) {
    if (!value || typeof value !== 'string') {
      continue;
    }
    if (/plate|registration|lpn/i.test(key)) {
      const plate = normalizePlate(value);
      if (plate) {
        return plate;
      }
    }
  }

  const plateFromPan = extractPlateFromPan(attributes.pan);
  if (plateFromPan) {
    return plateFromPan;
  }

  const plateFromMaskedPan = extractPlateFromPan(attributes.maskedPan);
  if (plateFromMaskedPan) {
    return plateFromMaskedPan;
  }

  const plateFromKey = normalizePlate(attributes.key);
  if (plateFromKey && looksLikePlate(plateFromKey)) {
    return plateFromKey;
  }

  const plateFromSearchKey = normalizePlate(searchKey);
  if (plateFromSearchKey) {
    return plateFromSearchKey;
  }

  return '-';
};

export const calculateTicketPricing = (attributes = {}, searchKey = '') => {
  const startDate = parseEventStart(attributes.eventDate);
  if (!startDate) {
    return null;
  }

  const endDate = getClosingTime(startDate);
  const hourlyRate = getHourlyRateFromRaw(attributes.amount);
  const billableHours = calculateBillableHours(startDate, endDate);
  const totalPayable = billableHours * hourlyRate;
  const currency = attributes.amountCurrency || 'NAD';

  return {
    vehiclePlate: getVehiclePlate(attributes, searchKey),
    parkingSlot: attributes?.params?.vehicles_parking_place_number || '-',
    hourlyRate,
    startDate,
    endDate,
    billableHours,
    totalPayable,
    currency,
    eventId: attributes.id,
    orderNumber: attributes.orderNumber,
    pan: attributes.pan,
    status: attributes.status || 'OPEN'
  };
};

export const formatNamibianDateTime = (date) => {
  if (!date) {
    return '-';
  }

  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) {
    return '-';
  }

  const day = String(value.getDate()).padStart(2, '0');
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const year = value.getFullYear();
  const hours = String(value.getHours()).padStart(2, '0');
  const minutes = String(value.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

export const formatDuration = (hours) => {
  if (hours === 1) {
    return '1 hour';
  }
  return `${hours} hours`;
};

export const formatCurrency = (amount, currency = 'NAD') => {
  const value = Number(amount);
  if (!Number.isFinite(value)) {
    return `${currency} -`;
  }
  return `${currency} ${value.toFixed(2)}`;
};

export const formatHourlyRate = (hourlyRate, currency = 'NAD') => {
  return `${formatCurrency(hourlyRate, currency)}/hour`;
};
